import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { useQuery } from '@apollo/client';
import type {
  GetGroupedWorklistItemsQuery,
  GetGroupedWorklistItemsQueryVariables,
} from 'generated/graphql';
import { useMemo } from 'react';
import { WithApolloContext } from 'modules/Apollo/types';
import { GET_GROUPED_WORKLIST_ITEMS } from 'modules/Apollo/queries';

export type UseCurrentCaseGroupSubmittingResult = Readonly<{
  submittingCaseInGroup: Nullable<GetGroupedWorklistItemsQuery['groupedWorklistItems'][0]>;
  hasSubmittingCaseInGroup: boolean;
  isCurrentCaseInSubmitWindow: boolean;
  submittedAt: Nullable<Date>;
  loading: boolean;
}>;

export const useCurrentCaseGroupSubmitting = ({
  context,
}: WithApolloContext): UseCurrentCaseGroupSubmittingResult => {
  const { currentCaseReport } = useCurrentCaseReport();
  const hasGroup = currentCaseReport?.groupId != null;

  const { data, loading: queryLoading } = useQuery<
    GetGroupedWorklistItemsQuery,
    GetGroupedWorklistItemsQueryVariables
  >(GET_GROUPED_WORKLIST_ITEMS, {
    fetchPolicy: 'cache-and-network',
    variables: { groupId: currentCaseReport?.groupId },
    skip: !hasGroup,
    context: {
      ...context,
      telemetry: { ...context.telemetry, fetchPolicy: 'cache-and-network' },
    },
  });

  const groupedWorklistItems = data?.groupedWorklistItems;
  const loading = hasGroup && groupedWorklistItems == null && queryLoading;

  const currentCaseInGroup = useMemo(
    () =>
      hasGroup && groupedWorklistItems != null && currentCaseReport != null
        ? (groupedWorklistItems.find((item) => item.smid === currentCaseReport.smid) ?? null)
        : null,
    [currentCaseReport, groupedWorklistItems, hasGroup]
  );

  const submittingCaseInGroup = useMemo(
    () =>
      currentCaseInGroup?.report?.submittedAt != null && currentCaseInGroup?.report?.sentAt == null
        ? currentCaseInGroup
        : (groupedWorklistItems?.find(
            (item) => item.report?.submittedAt != null && item.report?.sentAt == null
          ) ?? null),
    [currentCaseInGroup, groupedWorklistItems]
  );
  const hasSubmittingCaseInGroup = submittingCaseInGroup != null;
  const isCurrentCaseInSubmitWindow =
    currentCaseInGroup?.report?.submittedAt != null && currentCaseInGroup?.report?.sentAt == null;

  const submittedAt = hasSubmittingCaseInGroup ? submittingCaseInGroup?.report?.submittedAt : null;

  return useMemo(
    () => ({
      submittingCaseInGroup,
      hasSubmittingCaseInGroup,
      isCurrentCaseInSubmitWindow,
      submittedAt,
      loading,
    }),
    [
      submittingCaseInGroup,
      hasSubmittingCaseInGroup,
      isCurrentCaseInSubmitWindow,
      submittedAt,
      loading,
    ]
  );
};
